import { BaseApiService, ApiResponse } from './baseApi';
import { API_ENDPOINTS, HTTP_METHODS } from '../config/api';
import { rawItemApiService } from './rawItemApi';
import { toast } from 'sonner';
import { Supplier } from './supplierApi';
import { RawItem } from './rawItemApi';

// Purchase Order Header interface
export interface PoHeader {
  id: number;
  poNumber: string;
  purchaseDate: string;
  expectedDeliveryDate: string;
  actualDeliveryDate: string;
  paymentTerms: string;
  deliveryTerms: string;
  currency: string;
  exchangeRate: number;
  status: string;
  effectiveStartDate: string;
  effectiveEndDate: string;
  modeOfDespatch: string;
  transportationCharges: number;
  insuranceCharges: number;
  packingCharges: number;
  installAndServiceCharges: number;
  projectLeadTime: string;
  itemCostOthCharges: number;
  grandTotalTax: number;
  totalAmount: number;
  grandTotal: number;
  amendmentRevisionNumber: string;
  amendmentChangeDate: string;
  amendmentReason: string;
  approvedDate: string;
  approvedBy: number;
  signedBy: number;
  poType: string;
  priority: string;
  sourceType: string;
  vendorQuoteNo: string;
  vendorQuoteDate: string;
  isActive: boolean;
  branchId: number;
  shipToBranchId: number;
  createdBy: number;
  updatedBy: number;
  createdAt: string;
  updatedAt: string;
  supplier: Supplier;
  fullyReceived: boolean;
  partiallyReceived: boolean;
  delivered: boolean;
  totalReceivedQuantity: number;
  totalPendingQuantity: number;
  statusDisplay: string;
  overdue: boolean;
}

// Purchase Order Item Detail interface
export interface PoItemDetail {
  id: number;
  quantity: number;
  receivedQuantity: number;
  unitOfMeasure: string;
  itemDescription: string;
  unitPrice: number;
  discountPercentage: number;
  discountAmount: number;
  taxPercentage: number;
  taxAmount: number;
  totalAmount: number;
  notes: string;
  purchaseOrder: PoHeader;
  rawItemId: number;
  rawItem?: RawItem; // Optional: real server returns this, mock server doesn't
}

// Create request interfaces
export interface PoHeaderCreateRequest {
  poNumber: string;
  purchaseDate: string;
  expectedDeliveryDate: string;
  actualDeliveryDate?: string;
  paymentTerms: string;
  deliveryTerms: string;
  currency: string;
  exchangeRate: number | null;
  status: string;
  effectiveStartDate: string;
  effectiveEndDate: string;
  modeOfDespatch: string;
  transportationCharges: number;
  insuranceCharges: number;
  packingCharges: number;
  installAndServiceCharges: number;
  projectLeadTime: string;
  itemCostOthCharges: number;
  grandTotalTax: number;
  totalAmount: number;
  grandTotal: number;
  amendmentRevisionNumber?: string;
  amendmentChangeDate?: string;
  amendmentReason?: string;
  approvedDate?: string;
  approvedBy?: number | null;
  signedBy?: number | null;
  poType: string;
  priority: string;
  sourceType: string;
  vendorQuoteNo?: string;
  vendorQuoteDate?: string;
  isActive: boolean;
  branchId: number;
  shipToBranchId: number;
  createdBy: number;
  updatedBy: number;
  supplierId: number;
}

export interface PoItemDetailCreateRequest {
  quantity: number;
  receivedQuantity: number;
  unitOfMeasure: string;
  itemDescription: string;
  unitPrice: number;
  discountPercentage: number;
  discountAmount: number;
  taxPercentage: number;
  taxAmount: number;
  totalAmount: number;
  notes?: string;
  poHeaderId: number;
  rawItemId: number;
}

// Update request interfaces
export interface PoHeaderUpdateRequest extends PoHeaderCreateRequest {
  id: number;
}

export interface PoItemDetailUpdateRequest extends PoItemDetailCreateRequest {
  id: number;
}

// Last Transaction interface
export interface LastTransaction {
  poNumber?: string;
  poDate: string;
  supplierName: string;
  rate: number;
  rawItemId?: number; // Added to identify which item this transaction belongs to
}

// Batch request for last transactions
export interface RawItemTransactionsRequest {
  rawItemIds: number[];
  branchId?: number;
}

// Response for batch transactions (grouped by rawItemId)
export interface RawItemTransactionsResponse {
  [rawItemId: number]: LastTransaction[];
}

// API Service
export class PurchaseOrderApiService extends BaseApiService {
  // Purchase Order Headers
  async getPurchaseOrders(params?: { 
    branchId?: number;
    page?: number;
    pageSize?: number;
    search?: string;
    status?: string;
  }): Promise<{
    data: PoHeader[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalCount: number;
      pageSize: number;
    };
  }> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.branchId) {
        queryParams.append('branchId', params.branchId.toString());
      }
      if (params?.page) {
        queryParams.append('page', params.page.toString());
      }
      if (params?.pageSize) {
        queryParams.append('pageSize', params.pageSize.toString());
      }
      if (params?.search) {
        queryParams.append('search', params.search);
      }
      if (params?.status) {
        queryParams.append('status', params.status);
      }
      
      const url = queryParams.toString() 
        ? `${API_ENDPOINTS.PURCHASE_ORDERS.LIST}?${queryParams.toString()}`
        : API_ENDPOINTS.PURCHASE_ORDERS.LIST;
        
      const response = await this.makeRequest<{
        success: boolean;
        data: PoHeader[];
        pagination: {
          currentPage: number;
          totalPages: number;
          totalCount: number;
          pageSize: number;
        };
        message?: string;
      }>(
        url,
        { method: HTTP_METHODS.GET }
      );
      
      if (response.success) {
        return {
          data: response.data,
          pagination: response.pagination
        };
      } else {
        throw new Error(response.message || 'Failed to fetch purchase orders');
      }
    } catch (error) {
      // console.error('Error fetching purchase orders:', error);
      toast.error('Failed to fetch purchase orders');
      throw error;
    }
  }

  async getPurchaseOrderById(id: string): Promise<PoHeader> {
    try {
      const response = await this.makeRequest<ApiResponse<PoHeader>>(
        API_ENDPOINTS.PURCHASE_ORDERS.DETAIL(id),
        { method: HTTP_METHODS.GET }
      );
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch purchase order');
      }
    } catch (error) {
      // console.error('Error fetching purchase order:', error);
      toast.error('Failed to fetch purchase order details');
      throw error;
    }
  }

  // Get PO numbers for a specific branch (for GRN forms)
  async getPONumbersByBranch(branchId: number): Promise<PoHeader[]> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('branchId', branchId.toString());
      
      const url = `${API_ENDPOINTS.PURCHASE_ORDERS.PO_NUMBERS}?${queryParams.toString()}`;
      
      const response = await this.makeRequest<ApiResponse<PoHeader[]>>(
        url,
        { method: HTTP_METHODS.GET }
      );
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch PO numbers');
      }
    } catch (error) {
      // console.error('Error fetching PO numbers:', error);
      throw error;
    }
  }

  // Get approved PO numbers for a specific branch (for GRN forms)
  async getApprovedPONumbersByBranch(branchId: number): Promise<PoHeader[]> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('branchId', branchId.toString());
      
      const url = `${API_ENDPOINTS.PURCHASE_ORDERS.APPROVED_PO_NUMBERS}?${queryParams.toString()}`;
      
      const response = await this.makeRequest<ApiResponse<PoHeader[]>>(
        url,
        { method: HTTP_METHODS.GET }
      );
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch approved PO numbers');
      }
    } catch (error) {
      // console.error('Error fetching approved PO numbers:', error);
      throw error;
    }
  }

  // Search purchase orders
  async searchPurchaseOrders(params: {
    q: string;
    branchId: number;
  }): Promise<{
    data: PoHeader[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalCount: number;
      pageSize: number;
    };
  }> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('q', params.q);
      queryParams.append('branchId', params.branchId.toString());
      
      const url = `${API_ENDPOINTS.PURCHASE_ORDERS.SEARCH}?${queryParams.toString()}`;
      
      const response = await this.makeRequest<{
        success: boolean;
        data: PoHeader[];
        pagination?: {
          currentPage: number;
          totalPages: number;
          totalCount: number;
          pageSize: number;
        };
        message?: string;
      }>(
        url,
        { method: HTTP_METHODS.GET }
      );
      
      if (response.success) {
        // Handle case where search API might not return pagination info
        const orders = response.data || [];
        const pagination = response.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalCount: orders.length,
          pageSize: orders.length
        };
        
        return {
          data: orders,
          pagination: pagination
        };
      } else {
        throw new Error(response.message || 'Failed to search purchase orders');
      }
    } catch (error) {
      // console.error('Error searching purchase orders:', error);
      toast.error('Failed to search purchase orders');
      throw error;
    }
  }

  async createPurchaseOrder(data: PoHeaderCreateRequest): Promise<PoHeader> {
    try {
      const response = await this.makeRequest<ApiResponse<PoHeader>>(
        API_ENDPOINTS.PURCHASE_ORDERS.CREATE,
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(data)
        }
      );
      
      if (response.success) {
        toast.success('Purchase order created successfully');
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to create purchase order');
      }
    } catch (error) {
      // console.error('Error creating purchase order:', error);
      toast.error('Failed to create purchase order');
      throw error;
    }
  }

  async updatePurchaseOrder(id: string, data: PoHeaderUpdateRequest): Promise<PoHeader> {
    try {
      const response = await this.makeRequest<ApiResponse<PoHeader>>(
        API_ENDPOINTS.PURCHASE_ORDERS.UPDATE(id),
        {
          method: HTTP_METHODS.PUT,
          body: JSON.stringify(data)
        }
      );
      
      if (response.success) {
        toast.success('Purchase order updated successfully');
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to update purchase order');
      }
    } catch (error) {
      // console.error('Error updating purchase order:', error);
      toast.error('Failed to update purchase order');
      throw error;
    }
  }

  async updatePoStatus(id: string, status: string, userId: number): Promise<PoHeader> {
    try {
      const response = await this.makeRequest<ApiResponse<PoHeader>>(
        API_ENDPOINTS.PURCHASE_ORDERS.UPDATE_STATUS(id, status),
        {
          method: HTTP_METHODS.PUT,
          headers: {
            'X-User-ID': userId.toString()
          }
        }
      );
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to update purchase order status');
      }
    } catch (error) {
      // console.error('Error updating purchase order status:', error);
      throw error;
    }
  }

  async deletePurchaseOrder(id: string): Promise<void> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(
        API_ENDPOINTS.PURCHASE_ORDERS.DELETE(id),
        { method: HTTP_METHODS.DELETE }
      );
      
      if (response.success) {
        toast.success('Purchase order deleted successfully');
      } else {
        throw new Error(response.message || 'Failed to delete purchase order');
      }
    } catch (error) {
      // console.error('Error deleting purchase order:', error);
      toast.error('Failed to delete purchase order');
      throw error;
    }
  }

  // Purchase Order Item Details
  async getPoItemDetails(poId: string): Promise<PoItemDetail[]> {
    try {
      const response = await this.makeRequest<ApiResponse<PoItemDetail[]>>(
        API_ENDPOINTS.PURCHASE_ORDERS.ITEMS(poId),
        { method: HTTP_METHODS.GET }
      );
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch PO item details');
      }
    } catch (error) {
      // console.error('Error fetching PO item details:', error);
      toast.error('Failed to fetch PO item details');
      throw error;
    }
  }

  async getPoItemDetailById(id: string): Promise<PoItemDetail> {
    try {
      const response = await this.makeRequest<ApiResponse<PoItemDetail>>(
        API_ENDPOINTS.PURCHASE_ORDERS.ITEM_DETAIL(id),
        { method: HTTP_METHODS.GET }
      );
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch PO item detail');
      }
    } catch (error) {
      // console.error('Error fetching PO item detail:', error);
      toast.error('Failed to fetch PO item detail');
      throw error;
    }
  }

  async createPoItemDetail(data: PoItemDetailCreateRequest): Promise<PoItemDetail> {
    try {
      const response = await this.makeRequest<ApiResponse<PoItemDetail>>(
        API_ENDPOINTS.PURCHASE_ORDERS.CREATE_ITEM,
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(data)
        }
      );
      
      if (response.success) {
        toast.success('PO item detail created successfully');
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to create PO item detail');
      }
    } catch (error) {
      // console.error('Error creating PO item detail:', error);
      toast.error('Failed to create PO item detail');
      throw error;
    }
  }

  async updatePoItemDetail(id: string, data: PoItemDetailUpdateRequest): Promise<PoItemDetail> {
    try {
      const response = await this.makeRequest<ApiResponse<PoItemDetail>>(
        API_ENDPOINTS.PURCHASE_ORDERS.UPDATE_ITEM(id),
        {
          method: HTTP_METHODS.PUT,
          body: JSON.stringify(data)
        }
      );
      
      if (response.success) {
        toast.success('PO item detail updated successfully');
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to update PO item detail');
      }
    } catch (error) {
      // console.error('Error updating PO item detail:', error);
      toast.error('Failed to update PO item detail');
      throw error;
    }
  }

  async deletePoItemDetail(id: string): Promise<void> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(
        API_ENDPOINTS.PURCHASE_ORDERS.DELETE_ITEM(id),
        { method: HTTP_METHODS.DELETE }
      );
      
      if (response.success) {
        toast.success('PO item detail deleted successfully');
      } else {
        throw new Error(response.message || 'Failed to delete PO item detail');
      }
    } catch (error) {
      // console.error('Error deleting PO item detail:', error);
      toast.error('Failed to delete PO item detail');
      throw error;
    }
  }

  // Get last transactions for a raw item (single item - for info icon)
  async getLastTransactions(rawItemId: string, branchId?: number): Promise<ApiResponse<LastTransaction[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<LastTransaction[]>>(
        API_ENDPOINTS.PURCHASE_ORDERS.LAST_TRANSACTIONS(rawItemId, branchId),
        { method: HTTP_METHODS.GET }
      );

      if (response.success) {
        return response;
      } else {
        throw new Error(response.message || 'Failed to fetch last transactions');
      }
    } catch (error) {
      // console.error('Error fetching last transactions:', error);
      throw error;
    }
  }

  // Get last transactions for multiple raw items (batch - for analysis)
  async getLastTransactionsBatch(rawItemIds: number[], branchId?: number): Promise<ApiResponse<RawItemTransactionsResponse>> {
    try {
      const requestPayload: RawItemTransactionsRequest = {
        rawItemIds,
        ...(branchId && { branchId })
      };

      const response = await this.makeRequest<ApiResponse<RawItemTransactionsResponse>>(
        API_ENDPOINTS.PURCHASE_ORDERS.LAST_TRANSACTIONS_BATCH,
        { 
          method: HTTP_METHODS.POST,
          body: JSON.stringify(requestPayload)
        }
      );

      if (response.success) {
        return response;
      } else {
        throw new Error(response.message || 'Failed to fetch last transactions');
      }
    } catch (error) {
      // console.error('Error fetching batch last transactions:', error);
      throw error;
    }
  }

  async getStaticTerms(): Promise<string | string[]> {
    try {
      const response = await this.makeRequest<ApiResponse<string | string[]>>(
        API_ENDPOINTS.PURCHASE_ORDERS.STATIC_TERMS,
        {
          method: HTTP_METHODS.GET
        }
      );
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch static terms');
      }
    } catch (error) {
      // console.error('Error fetching static terms:', error);
      throw error;
    }
  }
}

export const purchaseOrderApiService = new PurchaseOrderApiService();
